import { CardStackIcon } from "@radix-ui/react-icons";
import { GitBranch } from "lucide-react";
import type { ReactNode } from "react";

import { SpanIcon } from "@/components/TraceDetail/SpanDetails/SpanIcon";
import type { TraceSpanLike } from "@/components/TraceDetail/SpanDetails/types";
import { getSpanDisplayType } from "@/components/TraceDetail/SpanDetails/utils";
import { cn } from "@/lib/utils";
import type { ColumnType } from "@/queries/smart-tables/types";
import {
  isRequestColumnType,
  isTraceColumnType,
} from "@/routes/smart-table/utils/columnTypeGuards";
import { parseAnchorCellValue } from "@/routes/smart-table/utils/parseAnchorCellValue";
import type { SpanNode } from "@/types/spans";

const toMinimalSpanNode = (spanLike: TraceSpanLike): SpanNode =>
  ({
    id: 0,
    name: spanLike.name,
    trace_id: "",
    span_id: "",
    parent_id: null,
    start: "",
    end: null,
    attributes: spanLike.attributes ?? {},
    resource: spanLike.resource ?? {},
    request_log_id: spanLike.request_log_id ?? null,
    workspace_id: 0,
    events: [],
    status: { status_code: "StatusCode.OK", message: "" },
    children: [],
  }) as SpanNode;

const TraceSidebarFallbackIcon = ({ className }: { className?: string }) => (
  <GitBranch className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground", className)} aria-hidden />
);

const RequestStackedIcon = ({ className }: { className?: string }) => (
  <CardStackIcon
    className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground", className)}
    aria-hidden
  />
);

export const TraceCellLeadingIcon = ({ rawValue }: { rawValue: unknown }) => {
  const { spanLike } = parseAnchorCellValue(rawValue);
  if (!spanLike) {
    return <TraceSidebarFallbackIcon />;
  }

  const hasTypedIcon = Boolean(getSpanDisplayType(spanLike) || spanLike.request_log_id);
  if (!hasTypedIcon) {
    return <TraceSidebarFallbackIcon />;
  }

  return <SpanIcon spanNode={toMinimalSpanNode(spanLike)} className="ml-0 h-4 w-4 shrink-0" />;
};

export const getAnchorCellLeadingIcon = ({
  columnType,
  metricKey,
  hasContent,
  rawValue,
}: {
  columnType?: ColumnType;
  metricKey?: string | null;
  hasContent: boolean;
  rawValue: unknown;
}): ReactNode | null => {
  if (!hasContent || metricKey) {
    return null;
  }

  if (isRequestColumnType(columnType)) {
    return <RequestStackedIcon />;
  }

  if (isTraceColumnType(columnType)) {
    return <TraceCellLeadingIcon rawValue={rawValue} />;
  }

  return null;
};
